/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 * Uses AWS Cognito via Amplify for user management.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
  type SignInInput,
  type SignUpInput,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

export interface User {
  id: string;
  email: string;
  name?: string;
  groups: string[];
  isAdmin: boolean;
  createdAt?: string;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<{ needsConfirmation: boolean }>;
  confirmRegistration: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  getIdToken: () => Promise<string | null>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Check for existing session on mount and listen for OAuth events
  useEffect(() => {
    checkAuthState();

    // Listen for OAuth sign-in events (e.g., after Google redirect)
    const hubListener = Hub.listen('auth', async ({ payload }) => {
      console.log('[AuthContext] Hub event:', payload.event);
      switch (payload.event) {
        case 'signedIn':
        case 'signInWithRedirect':
          // User signed in via OAuth redirect
          await loadUserDetails();
          break;
        case 'signedOut':
          setState({ user: null, isLoading: false, isAuthenticated: false });
          break;
        case 'signInWithRedirect_failure':
          console.error('[AuthContext] OAuth sign-in failed:', payload.data);
          setState(prev => ({ ...prev, isLoading: false }));
          break;
      }
    });

    return () => {
      hubListener();
    };
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        await loadUserDetails();
      } else {
        setState({ user: null, isLoading: false, isAuthenticated: false });
      }
    } catch {
      // No authenticated user
      setState({ user: null, isLoading: false, isAuthenticated: false });
    }
  };

  const loadUserDetails = async (): Promise<User | null> => {
    try {
      const [session, attributes] = await Promise.all([
        fetchAuthSession(),
        fetchUserAttributes(),
      ]);

      // Extract groups from the ID token
      const groups = (session.tokens?.idToken?.payload?.['cognito:groups'] as string[]) || [];
      const isAdmin = groups.includes('admins');

      // Get auth_time from token (Unix timestamp of when user was created/first authenticated)
      const authTime = session.tokens?.idToken?.payload?.['auth_time'] as number | undefined;
      const createdAt = authTime ? new Date(authTime * 1000).toISOString() : undefined;

      const user: User = {
        id: attributes.sub || '',
        email: attributes.email || '',
        name: attributes.name,
        groups,
        isAdmin,
        createdAt,
      };

      setState({
        user,
        isLoading: false,
        isAuthenticated: true,
      });

      return user;
    } catch (error) {
      console.error('Failed to load user details:', error);
      setState({ user: null, isLoading: false, isAuthenticated: false });
      return null;
    }
  };

  const login = useCallback(async (email: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const signInInput: SignInInput = {
        username: email,
        password,
      };

      const result = await signIn(signInInput);

      if (result.isSignedIn) {
        await loadUserDetails();
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
        setState(prev => ({ ...prev, isLoading: false }));
        throw new Error('Please confirm your email before signing in.');
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
        throw new Error('Additional authentication steps required.');
      }
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await signInWithRedirect({ provider: 'Google' });
      // Note: This will redirect to Google, so we won't reach here until callback
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const register = useCallback(async (email: string, password: string, name?: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const signUpInput: SignUpInput = {
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            ...(name && { name }),
          },
        },
      };

      const result = await signUp(signUpInput);
      setState(prev => ({ ...prev, isLoading: false }));

      return {
        needsConfirmation: !result.isSignUpComplete,
      };
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const confirmRegistration = useCallback(async (email: string, code: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
      setState(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await signOut();
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await resetPassword({ username: email });
  }, []);

  const confirmForgotPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  const getIdToken = useCallback(async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    await loadUserDetails();
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    loginWithGoogle,
    register,
    confirmRegistration,
    logout,
    forgotPassword,
    confirmForgotPassword,
    getAccessToken,
    getIdToken,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
